import { QueryClient } from '@tanstack/react-query'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { useMemo } from 'react'

import { getBaseUrl } from '../utils/getBaseUrl'
import { useSupabase } from '../utils/supabase/useSupabase'

// Custom hook to create an Axios client with a Supabase auth token
export const useAxiosClient = () => {
  const supabase = useSupabase()

  // Create an Axios client that includes the Supabase auth token in requests
  const axiosClient = useMemo(() => {
    const instance = axios.create({
      baseURL: `${getBaseUrl()}/api/v1`,
      timeout: 60000, // 60 seconds
    })

    // Add a request interceptor to include the Supabase token
    instance.interceptors.request.use(async (config) => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession()

        // If no valid session with user ID, silently cancel the request
        if (!session?.user?.id) {
          // Create a canceled request by using the signal property
          const controller = new AbortController()
          controller.abort()
          config.signal = controller.signal
          return config
        }

        if (session?.access_token) {
          config.headers['Authorization'] = `Bearer ${session.access_token}`
        }

        return config
      } catch (error) {
        // Silently cancel without logging
        const controller = new AbortController()
        controller.abort()
        config.signal = controller.signal
        return config
      }
    })

    // Add a response interceptor to handle canceled requests silently
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        // Check if this is a canceled request
        if (axios.isCancel(error)) {
          // Return a resolved promise to prevent the error from propagating
          return Promise.resolve({
            status: 499, // Client Closed Request (unofficial)
            statusText: 'Request canceled',
            headers: {},
            config: {},
            data: null,
          })
        }

        // Also check for other cancellation indicators
        const err = error as any
        if (
          err.name === 'AbortError' ||
          err.name === 'CanceledError' ||
          err.message === 'canceled'
        ) {
          return Promise.resolve({
            status: 499,
            statusText: 'Request canceled',
            headers: {},
            config: err.config || {},
            data: null,
          })
        }

        // Let other errors propagate
        return Promise.reject(error)
      }
    )

    return instance
  }, [supabase])

  return axiosClient
}

// Create a QueryClient instance
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})
